import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Moon, Sun, X, Loader2, MailCheck } from 'lucide-react';
import { usersAPI, authAPI } from '../services/api';

const A2G_LOGO = "https://a2gdesigns.com/icons/icon-512x512.png";

const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await usersAPI.requestPasswordReset(email, window.location.origin);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="text-center py-4">
            <MailCheck className="w-10 h-10 text-blue-900 dark:text-orange-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Check your email</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              If <strong>{email}</strong> is registered, we've sent a link to reset your password. It expires in 24 hours.
            </p>
            <Button onClick={onClose} className="w-full mt-5 bg-blue-900 hover:bg-blue-800 dark:bg-orange-600 dark:hover:bg-orange-500 text-white">
              Back to Sign In
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Reset your password</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600"
                required
                autoFocus
              />
              <Button type="submit" disabled={submitting} className="w-full bg-blue-900 hover:bg-blue-800 dark:bg-orange-600 dark:hover:bg-orange-500 text-white">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {submitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      const staffRoles = ['admin', 'superuser', 'sales', 'support_lvl_2', 'support_lvl_1'];
      if (staffRoles.includes(result.user.role)) {
        if (result.user.role === 'support_lvl_1' || result.user.role === 'support_lvl_2') {
          navigate('/admin/support-dashboard');
        } else {
          navigate('/admin');
        }
      } else {
        navigate('/client');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 transition-colors duration-300 px-4">
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white dark:bg-slate-800 shadow-md hover:shadow-lg transition-all"
      >
        {isDark ? <Sun className="w-5 h-5 text-orange-500" /> : <Moon className="w-5 h-5 text-slate-700" />}
      </button>
      
      <Card className="w-full max-w-md shadow-xl border-0 bg-white dark:bg-slate-800">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 mb-4">
            <img 
              src={A2G_LOGO} 
              alt="A2G Designs" 
              className="w-full h-full object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">Welcome Back</CardTitle>
          <CardDescription className="text-slate-500 dark:text-slate-400">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                {error}
                {/verify your email/i.test(error) && email && (
                  <button
                    type="button"
                    className="block mt-2 underline font-medium"
                    onClick={async () => {
                      try { await authAPI.resendVerification(email); } catch (_) { /* same answer either way */ }
                      setError('If that email has an unverified account, we sent a new verification link.');
                    }}
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-blue-900 dark:focus:border-orange-500"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-blue-900 dark:focus:border-orange-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded border-gray-300 dark:border-slate-600" />
                <span className="text-slate-600 dark:text-slate-400">Remember me</span>
              </label>
              <button type="button" onClick={() => setShowForgotModal(true)} className="text-blue-900 dark:text-orange-500 hover:underline">Forgot password?</button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-900 hover:bg-blue-800 dark:bg-orange-600 dark:hover:bg-orange-500 text-white font-medium py-2.5"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
</CardContent>
      </Card>

      {showForgotModal && <ForgotPasswordModal onClose={() => setShowForgotModal(false)} />}
    </div>
  );
};

export default Login;
